// dailyRewards.js
import { db, functions } from '@/config/firebase';
import { doc, getDoc, updateDoc, collection, runTransaction, Timestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { InventorySingleton } from './inventory';

const DAILY_REWARD_POOL_COLLECTION = 'dailyRewardPools';
const USER_CONTRIBUTIONS_SUBCOLLECTION = 'contributions';
const FLYING_ISLANDS_COLLECTION = "flyingIslands"
const DAILY_ISLAND_REWARD_AMOUNT = 5000;
const DAILY_REWARD_AMOUNT = 500;
const USERS_COLLECTION = 'users';

/**
 * Allows a user to claim their daily Bananium earnings from inventory.
 * Can only be claimed once per day.
 * @param {string} userId The ID of the user claiming.
 * @returns {Promise<number>} The amount of Bananium claimed, or throws an error if already claimed or failed.
 */
const claimDailyInventoryBananiumCallable = httpsCallable(functions, 'claimDailyInventoryBananium');

const contributeBananiumCallable = httpsCallable(functions, 'contributeBananium');

// Bananium value per voxel based on rarity
const RARITY_BANANIUM_VALUES = {
    common: 1,
    uncommon: 1,
    rare: 1,
    epic: 1,
    legendary: 1
};

// Helper to get the current date in a YYYY-MM-DD format
function getCurrentDateString() {
    const today = new Date();
    const year = today.getFullYear();
    const month = (today.getMonth() + 1).toString().padStart(2, '0');
    const day = today.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
}

// Get the Firestore document reference for the current day's reward pool
function getCurrentDailyPoolRef() {
    const todayString = getCurrentDateString();
    return doc(db, DAILY_REWARD_POOL_COLLECTION, todayString);
}

// Get the Firestore document reference for a user's contribution today
function getUserDailyContributionRef(userId) {
    const todayPoolRef = getCurrentDailyPoolRef();
    return doc(collection(todayPoolRef, USER_CONTRIBUTIONS_SUBCOLLECTION), userId);
}

/**
 * Allows a user to contribute a specified amount of Bananium to the daily pool.
 * Uses a transaction to ensure atomicity when updating user balance,
 * user contribution, and the total pool amount.
 * @param {string} userId The ID of the contributing user.
 * @param {number} amount The amount of Bananium to contribute.
 * @returns {Promise<void>}
 */
async function contributeToPool(amount) {
    try {
        if (amount <= 0) {
            throw new Error("Contribution amount must be positive.");
        }

        await contributeBananiumCallable({
            amount
        })
        console.log("Contribution successful.");

    } catch (e) {
        console.error("Contribution failed: ", e);
        throw e;
    }
}


/**
 * Calculates the potential daily Bananium earnings from a user's USED inventory.
 * This function is intended for client-side use to display potential earnings.
 * The server will re-calculate the amount during the actual claim for security.
 * @param {object} voxelInventory The user's voxelInventory object from their user document.
 * @returns {number} The total Bananium amount the user can claim from their USED inventory today.
 */
function calculateDailyInventoryBananium(voxelInventory) {
    let totalBananium = 0;

    // Define the order of rarities and their Bananium values (should match server)
    const RARITY_ORDER = ['common', 'uncommon', 'rare', 'epic', 'legendary'];
    const RARITY_BANANIUM_VALUES = {
        common: 1, uncommon: 1, rare: 1, epic: 1, legendary: 1
    };

    // Ensure voxelInventory and its used section are valid
    if (!voxelInventory || !voxelInventory.used) {
        console.warn("[calculateDailyInventoryBananium] Invalid or missing voxelInventory.used data.");
        return 0;
    }

    const usedInventory = voxelInventory.used;

    for (const rarity of RARITY_ORDER) { // Iterate through defined rarities
        const voxelCounts = usedInventory[rarity];

        if (voxelCounts) { // Check if the rarity exists in the used inventory
            const rarityValue = RARITY_BANANIUM_VALUES[rarity];

            if (rarityValue !== undefined) { // Ensure rarity has a defined Bananium value
                for (const voxelType in voxelCounts) {
                    // Check if the property is directly on the object and the count is valid
                    if (Object.prototype.hasOwnProperty.call(voxelCounts, voxelType)) {
                        const count = voxelCounts[voxelType];
                        if (typeof count === 'number' && count > 0) {
                            totalBananium += count * rarityValue;
                        }
                    }
                }
            } else {
                console.warn(`[calculateDailyInventoryBananium] No Bananium value defined for rarity: ${rarity}`);
            }
        }
    }

    return totalBananium;
}

/**
 * Calls the server-side function to claim daily Bananium earnings from inventory.
 * @returns {Promise<{success: boolean, claimedAmount?: number}>} A promise resolving with the result from the Cloud Function.
 */
async function claimDailyInventoryBananium() {
    try {
        console.log("[Client] Attempting to claim daily inventory Bananium...");

        // Call the Cloud Function. No data needs to be sent as userId is
        // obtained from the auth context on the server.
        const result = await claimDailyInventoryBananiumCallable();

        console.log("[Client] Daily inventory claim function call successful:", result.data);

        // The server function returns { success: true, claimedAmount: number } on success
        if (result.data && result.data.success) {
            console.log(`[Client] Successfully claimed ${result.data.claimedAmount} Bananium.`);
            return result.data; // Return the success status and claimed amount
        } else {
            // Handle cases where success is false or data is unexpected
            console.error("[Client] Daily inventory claim failed on server:", result.data);
            throw new Error("Daily inventory claim failed."); // Throw an error to be caught by the caller
        }

    } catch (error) {
        console.error("[Client] Error calling daily inventory claim function:", error);
        // Re-throw the error or return a specific error object
        throw error;
    }
}

const claimDailyIslandRewardCallable = httpsCallable(functions, 'claimDailyIslandReward');

async function claimDailyIslandReward(userId, islandId) {
    try {
        console.log(`[Client] User ${userId} attempting to claim reward for island ${islandId}...`);
        const result = await claimDailyIslandRewardCallable({ islandId });

        if (result.data && result.data.success) {
            console.log(`[Client] Successfully claimed ${result.data.claimedAmount} Bananium.`);
            return result.data.claimedAmount;
        } else {
            throw new Error(result.data?.message || "Claim failed.");
        }
    } catch (e) {
        console.error("Daily island reward claim failed: ", e);
        throw e;
    }
}


// Export functions
export {
    contributeToPool,
    getCurrentDailyPoolRef,
    getUserDailyContributionRef,
    DAILY_REWARD_AMOUNT,
    DAILY_ISLAND_REWARD_AMOUNT,
    getCurrentDateString,
    calculateDailyInventoryBananium, // Export for use in UI to display potential claim
    claimDailyInventoryBananium,
    claimDailyIslandReward
};
